import Button from "@components/Button"
import OrderSummary from "@components/OrderSummary"
import TermsCheckbox from "@components/TermsCheckbox"
import {
  resolveConfidenceVisitorId,
  VISITOR_ID_QUERY_PARAM,
} from "@helpers/confidenceVisitorId"
import { trackSubscriptionEvent } from "@helpers/facebookTracking"
import { getOnboardingContext } from "@helpers/localStorage"
import {
  getConfirmPaymentErrorMessage,
  getLastPaymentErrorMessage,
} from "@helpers/stripeErrors"
import * as Sentry from "@sentry/react"
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { PaymentIntent } from "@stripe/stripe-js"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { Plan } from "src/types"

interface Props {
  clientSecret: string
  currency: string
  fullPrice: number
  plan: Plan
  stripeFirstPriceId: string
  stripeNextPriceId: string
  subscriptionId: string
  paymentIntentId?: string
  userEmail: string | undefined
}

export default function CheckoutForm({
  clientSecret,
  fullPrice,
  plan,
  stripeFirstPriceId,
  stripeNextPriceId,
  subscriptionId,
  paymentIntentId,
  userEmail,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false)
  const [message, setMessage] = useState<string | undefined>(undefined)
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | undefined>(
    undefined,
  )

  const elements = useElements()
  const location = useLocation()
  const stripe = useStripe()
  const { t } = useTranslation()
  const context = getOnboardingContext()
  const paymentCopyKey =
    context === "cars"
      ? "pages.checkout.paymentCars"
      : context === "pets"
        ? "pages.checkout.paymentPets"
        : "pages.checkout.payment"

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!elements || !stripe) return

    setIsSubmitting(true)

    const origin = window.location.origin
    const pathname = "/checkout/payment-completion"
    const urlSearchParams = new URLSearchParams(location.search)

    urlSearchParams.append("plan", plan)
    urlSearchParams.append("stripe_first_price_id", stripeFirstPriceId)
    urlSearchParams.append("stripe_next_price_id", stripeNextPriceId)
    urlSearchParams.append("subscription_id", subscriptionId)
    if (paymentIntentId) {
      urlSearchParams.append("payment_intent_id", paymentIntentId)
    }

    // Confirming payment hands the browser to Stripe, and coming back is a
    // fresh page load that has to find the visitor id again. Measured on Lingo
    // web, 23% of purchases find nothing there and mint a new id, so the
    // purchase lands on a visitor with no funnel behind it. Passing it through
    // the return URL is the only carrier the browser cannot drop.
    //
    // Cleared before writing, and written with `set` rather than `append`,
    // because these params start as a copy of the current URL's. A visitor
    // arriving on an attacker-crafted `?cnfd_visitor_id=` would otherwise carry
    // it into the return URL, and since `URLSearchParams.get` returns the first
    // of a duplicated key, theirs would win over ours on the way back. The
    // delete also covers the case where we have no id to send: an inbound one
    // must not survive on its own.
    urlSearchParams.delete(VISITOR_ID_QUERY_PARAM)

    const confidenceVisitorId = resolveConfidenceVisitorId()
    if (confidenceVisitorId) {
      urlSearchParams.set(VISITOR_ID_QUERY_PARAM, confidenceVisitorId)
    }

    const returnUrl = `${origin}${pathname}?${urlSearchParams.toString()}`

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
      })

      // Without an error the browser is on its way to the return URL.
      if (error) setMessage(getConfirmPaymentErrorMessage(error, t))
    } catch (error) {
      // An integration error (e.g. no mounted Payment Element) is not the
      // user's to fix, but they must not be left with a stuck button.
      Sentry.captureException(error)
      setMessage(t("components.checkoutForm.somethingWentWrong"))
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (!clientSecret || !stripe) {
      return
    }

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }) => {
        setPaymentIntent(paymentIntent)

        if (paymentIntent?.last_payment_error) {
          setMessage(
            getLastPaymentErrorMessage(
              paymentIntent.last_payment_error.code,
              t,
            ),
          )
        }
      })
      .catch((error) => {
        throw error
      })
  }, [clientSecret, setPaymentIntent, stripe, t, plan])

  useEffect(() => {
    if (!paymentIntent) return

    const trackEvent = async () => {
      await trackSubscriptionEvent({
        currency: paymentIntent.currency,
        isPurchase: false,
        planPrice: paymentIntent.amount,
        stripeFirstPriceId,
        stripeNextPriceId,
      })
    }

    trackEvent().catch((error) => Sentry.captureException(error))
  }, [paymentIntent, stripeFirstPriceId, stripeNextPriceId])

  useEffect(() => {
    const resetSubmittingState = () => {
      setIsSubmitting(false)
    }

    window.addEventListener("pageshow", resetSubmittingState)
    document.addEventListener("visibilitychange", resetSubmittingState)

    return () => {
      window.removeEventListener("pageshow", resetSubmittingState)
      document.removeEventListener("visibilitychange", resetSubmittingState)
    }
  }, [])

  return (
    <form
      className="flex grow flex-col justify-between space-y-4"
      onSubmit={handleSubmit}
    >
      <div className="rounded-3xl bg-white p-5">
        <PaymentElement
          options={{
            defaultValues: {
              billingDetails: {
                email: userEmail,
              },
            },
            layout: {
              defaultCollapsed: false,
              type: "tabs",
            },
            paymentMethodOrder: ["apple_pay", "card", "paypal", "google_pay"],
            wallets: {
              link: "auto",
            },
          }}
        />
      </div>

      <div className="space-y-6">
        <OrderSummary
          fullPrice={fullPrice}
          paymentIntent={paymentIntent}
          plan={plan}
        />

        <TermsCheckbox
          fullPrice={fullPrice}
          isTermsAccepted={isTermsAccepted}
          paymentIntent={paymentIntent}
          plan={plan}
          setIsTermsAccepted={setIsTermsAccepted}
        />

        {message && (
          <p className="theme-text-error text-center text-lg font-semibold">
            {message}
          </p>
        )}

        <Button
          disabled={!elements || !stripe || !isTermsAccepted || isSubmitting}
          title={t(`${paymentCopyKey}.submitPayment`)}
          type="submit"
        />

        <p className="text-center text-xs">
          {t(`${paymentCopyKey}.termsCheckboxHint`)}
        </p>
      </div>
    </form>
  )
}
